import { useQuery } from '@tanstack/react-query'
import { BookOpen, MoreHorizontal, Pencil, Plus, Trash2, Wand2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { errorMessage } from '@/api/errors'
import { EmptyState, ErrorState } from '@/components/feedback/States'
import { toast } from '@/components/feedback/toast-store'
import { Button, IconButton } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu'
import { Skeleton } from '@/components/ui/Skeleton'
import { Can } from '@/features/auth/guards'
import { formatDate } from '@/utils/format'
import { papersQuery, useDeletePaper } from '../api'
import { formatMarks } from '../status'
import type { Exam, Paper } from '../types'
import { GeneratePapersDialog, PaperDialog } from './PaperDialogs'
import { ProgressMeter } from './ProgressMeter'

type DialogState =
  | { kind: 'add'; classId: number }
  | { kind: 'edit'; paper: Paper }
  | { kind: 'delete'; paper: Paper }
  | { kind: 'generate' }
  | null

/** Papers of an exam grouped by class, with per-paper mark-entry progress. */
export function PapersPanel({ exam }: { exam: Exam }) {
  const { t } = useTranslation('exams')
  const papers = useQuery(papersQuery(exam.id))
  const remove = useDeletePaper()
  const [dialog, setDialog] = useState<DialogState>(null)
  const editable = ['draft', 'scheduled', 'marks_open'].includes(exam.status)
  const close = (open: boolean) => (open ? undefined : setDialog(null))

  if (papers.isPending) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 2 }, (_, i) => (
          <Card key={i} className="space-y-3 p-5">
            <Skeleton className="h-5 w-1/4" />
            <Skeleton className="h-3.5 w-full" />
            <Skeleton className="h-3.5 w-2/3" />
          </Card>
        ))}
      </div>
    )
  }
  if (papers.error)
    return (
      <Card>
        <ErrorState onRetry={() => void papers.refetch()} />
      </Card>
    )

  const byClass = exam.classes.map((c) => ({ cls: c, papers: papers.data.filter((p) => p.classId === c.id) }))

  return (
    <div className="space-y-5">
      {editable ? (
        <Can permission="exams.manage">
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="secondary" leftIcon={<Wand2 />} onClick={() => setDialog({ kind: 'generate' })}>
              {t('Create from curriculum')}
            </Button>
          </div>
        </Can>
      ) : null}
      {papers.data.length === 0 ? (
        <Card>
          <EmptyState
            icon={<BookOpen />}
            title={t('No papers yet')}
            description={t(
              "Create one paper per subject from each class's curriculum, then adjust dates and marks.",
            )}
            action={
              editable ? (
                <Can permission="exams.manage">
                  <Button leftIcon={<Wand2 />} onClick={() => setDialog({ kind: 'generate' })}>
                    {t('Create from curriculum')}
                  </Button>
                </Can>
              ) : undefined
            }
          />
        </Card>
      ) : null}
      {byClass
        .filter((g) => g.papers.length > 0 || papers.data.length > 0)
        .map(({ cls, papers: list }) => (
          <Card key={cls.id}>
            <CardHeader
              title={cls.name}
              description={`${t('{{count}} papers', { count: list.length })} · ${t('{{count}} sections', { count: cls.sections.length })}`}
              actions={
                editable ? (
                  <Can permission="exams.manage">
                    <Button
                      size="sm"
                      variant="soft"
                      leftIcon={<Plus />}
                      onClick={() => setDialog({ kind: 'add', classId: cls.id })}
                    >
                      {t('Add paper')}
                    </Button>
                  </Can>
                ) : null
              }
            />
            {list.length === 0 ? (
              <p className="px-5 py-4 text-sm text-muted">{t('No papers for this class.')}</p>
            ) : (
              <div className="scrollbar-thin overflow-x-auto">
                <table className="w-full border-separate border-spacing-0 text-sm">
                  <caption className="sr-only">{t('Papers of {{name}}', { name: cls.name })}</caption>
                  <thead>
                    <tr className="text-left text-2xs font-semibold tracking-wider text-muted uppercase">
                      <th scope="col" className="border-b border-line bg-surface-muted px-4 py-2.5 pl-5">
                        {t('Subject')}
                      </th>
                      <th
                        scope="col"
                        className="hidden border-b border-line bg-surface-muted px-4 py-2.5 sm:table-cell"
                      >
                        {t('Date')}
                      </th>
                      <th
                        scope="col"
                        className="border-b border-line bg-surface-muted px-4 py-2.5 text-right"
                      >
                        {t('Max')}
                      </th>
                      <th
                        scope="col"
                        className="hidden border-b border-line bg-surface-muted px-4 py-2.5 text-right sm:table-cell"
                      >
                        {t('Pass', { context: 'mark' })}
                      </th>
                      <th
                        scope="col"
                        className="hidden w-56 border-b border-line bg-surface-muted px-4 py-2.5 md:table-cell"
                      >
                        {t('Marks entered')}
                      </th>
                      <th scope="col" className="border-b border-line bg-surface-muted px-4 py-2.5 pr-5">
                        <span className="sr-only">{t('Actions')}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((p) => (
                      <tr key={p.id}>
                        <td className="border-b border-line px-4 py-3 pl-5">
                          <span className="flex items-center gap-2 font-medium text-ink">
                            <span
                              aria-hidden="true"
                              className="size-2.5 shrink-0 rounded-full bg-brand-500"
                              style={p.subjectColor ? { backgroundColor: p.subjectColor } : undefined}
                            />
                            {p.subjectName}
                            {p.subjectCode ? (
                              <span className="text-xs font-normal text-muted">{p.subjectCode}</span>
                            ) : null}
                          </span>
                        </td>
                        <td className="hidden border-b border-line px-4 py-3 text-ink-soft sm:table-cell">
                          {formatDate(p.date)}
                        </td>
                        <td className="border-b border-line px-4 py-3 text-right tabular">
                          {formatMarks(p.maxMarks)}
                        </td>
                        <td className="hidden border-b border-line px-4 py-3 text-right tabular sm:table-cell">
                          {formatMarks(p.passMarks)}
                        </td>
                        <td className="hidden border-b border-line px-4 py-3 md:table-cell">
                          <ProgressMeter
                            entered={p.marksEntered}
                            expected={p.marksExpected}
                            label={t('{{subject}} marks', { subject: p.subjectName })}
                          />
                        </td>
                        <td className="border-b border-line px-4 py-3 pr-5 text-right">
                          {editable ? (
                            <Can permission="exams.manage">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <IconButton
                                    label={t('Actions for {{name}}', { name: p.subjectName })}
                                    icon={<MoreHorizontal />}
                                    size="icon-sm"
                                  />
                                </DropdownMenuTrigger>
                                <DropdownMenuContent>
                                  <DropdownMenuItem
                                    icon={<Pencil />}
                                    onSelect={() => setDialog({ kind: 'edit', paper: p })}
                                  >
                                    {t('Edit')}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    icon={<Trash2 />}
                                    tone="danger"
                                    disabled={p.marksEntered > 0}
                                    onSelect={() => setDialog({ kind: 'delete', paper: p })}
                                  >
                                    {t('Remove')}
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </Can>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        ))}

      {dialog?.kind === 'add' ? (
        <PaperDialog exam={exam} classId={dialog.classId} open onOpenChange={close} />
      ) : null}
      {dialog?.kind === 'edit' ? (
        <PaperDialog exam={exam} paper={dialog.paper} open onOpenChange={close} />
      ) : null}
      {dialog?.kind === 'generate' ? <GeneratePapersDialog exam={exam} open onOpenChange={close} /> : null}
      {dialog?.kind === 'delete' ? (
        <ConfirmDialog
          open
          onOpenChange={close}
          title={t('Remove {{subject}} from {{className}}?', {
            subject: dialog.paper.subjectName,
            className: dialog.paper.className,
          })}
          description={t('The paper is removed from this exam. This cannot be undone.')}
          confirmLabel={t('Remove paper')}
          loading={remove.isPending}
          onConfirm={() =>
            remove.mutate(
              { examId: exam.id, id: dialog.paper.id },
              {
                onSuccess: () => {
                  toast.success(t('Paper removed'))
                  setDialog(null)
                },
                onError: (e) => toast.error(t('Could not remove the paper'), errorMessage(e)),
              },
            )
          }
        />
      ) : null}
    </div>
  )
}
